import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { ChartDataPoint, ChartView } from '@/hooks/useChartData'
import { PlanData } from '@/hooks/useStudioAnalytics'
import { useChartAxisTheme } from '@/hooks/useThemeColors'
import { formatNumber } from '@/lib/utils'

export interface VolumeChartProps {
  chartData: ChartDataPoint[]
  chartView: ChartView
  planData: PlanData | null
  visibleLines: Record<string, boolean>
}

export function VolumeChart({
  chartData,
  chartView,
  planData,
  visibleLines
}: VolumeChartProps) {
  // Сетка и оси recharts красятся SVG-атрибутами — CSS-переменная там не разрешается.
  const chartTheme = useChartAxisTheme()

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <CartesianGrid {...chartTheme.grid} />
          <XAxis dataKey="date" {...chartTheme.axis} />
          <YAxis {...chartTheme.axis} />
          <Tooltip
            labelFormatter={(label, payload) => {
              if (payload && payload[0]) {
                const date = payload[0].payload.date
                return chartView === 'cumulative' ? `${date} (накопительно)` : date
              }
              return label
            }}
            content={(props: any) => {
              if (!props.active || !props.payload) return null
              
              // Фильтруем только неплановые линии
              const filteredPayload = props.payload.filter((item: any) => 
                !item.dataKey?.startsWith('plan')
              )
              
              if (filteredPayload.length === 0) return null
              
              const labels: Record<string, string> = 
                chartView === 'daily' 
                  ? {
                      leads: 'Заявки',
                      appointments: 'Записи',
                      visitors: 'Приходы',
                      sales: 'Продажи'
                    }
                  : {
                      cumLeads: 'Заявки',
                      cumAppointments: 'Записи',
                      cumVisitors: 'Приходы',
                      cumSales: 'Продажи'
                    }
              
              return (
                <div className="bg-card text-foreground p-2 border border-border rounded shadow-sm">
                  <p className="text-sm font-medium mb-1">{props.label}</p>
                  {filteredPayload.map((item: any, index: number) => {
                    const payload = props.payload[0].payload
                    const label = labels[item.dataKey] || item.dataKey
                    let displayValue = formatNumber(item.value)
                    
                    // Добавляем план если в накопительном режиме
                    if (chartView === 'cumulative' && item.dataKey?.startsWith('cum') && planData) {
                      let planValue = null
                      if (item.dataKey === 'cumLeads') planValue = payload.planLeads
                      else if (item.dataKey === 'cumAppointments') planValue = payload.planAppointments
                      else if (item.dataKey === 'cumVisitors') planValue = payload.planVisitors
                      else if (item.dataKey === 'cumSales') planValue = payload.planSales
                      
                      if (planValue) {
                        displayValue = `${formatNumber(item.value)}/${formatNumber(planValue)}`
                      }
                    }
                    
                    return (
                      <p key={index} className="text-xs" style={{ color: item.color }}>
                        {label}: {displayValue}
                      </p>
                    )
                  })}
                </div>
              )
            }}
          />
          <Line
            type="monotone"
            dataKey={chartView === 'daily' ? 'leads' : 'cumLeads'}
            name={chartView === 'daily' ? 'leads' : 'cumLeads'}
            stroke="#FF9800"
            strokeWidth={2}
            dot={{ r: 3 }}
            opacity={visibleLines.leads ? 1 : 0.2}
            strokeDasharray={visibleLines.leads ? '0' : '5 5'}
          />
          <Line
            type="monotone"
            dataKey={chartView === 'daily' ? 'appointments' : 'cumAppointments'}
            name={chartView === 'daily' ? 'appointments' : 'cumAppointments'}
            stroke="#9C27B0"
            strokeWidth={2}
            dot={{ r: 3 }}
            opacity={visibleLines.appointments ? 1 : 0.2}
            strokeDasharray={visibleLines.appointments ? '0' : '5 5'}
          />
          <Line
            type="monotone"
            dataKey={chartView === 'daily' ? 'visitors' : 'cumVisitors'}
            name={chartView === 'daily' ? 'visitors' : 'cumVisitors'}
            stroke="#2196F3"
            strokeWidth={2}
            dot={{ r: 3 }}
            opacity={visibleLines.visitors ? 1 : 0.2}
            strokeDasharray={visibleLines.visitors ? '0' : '5 5'}
          />
          <Line
            type="monotone"
            dataKey={chartView === 'daily' ? 'sales' : 'cumSales'}
            name={chartView === 'daily' ? 'sales' : 'cumSales'}
            stroke="#E91E63"
            strokeWidth={2}
            dot={{ r: 3 }}
            opacity={visibleLines.sales ? 1 : 0.2}
            strokeDasharray={visibleLines.sales ? '0' : '5 5'}
          />
          {/* Plan lines (только в накопительном режиме) */}
          {chartView === 'cumulative' && planData && (
            <>
              <Line
                type="monotone"
                dataKey="planLeads"
                stroke="#FF9800"
                strokeWidth={1.5}
                dot={false}
                activeDot={false}
                strokeDasharray="5 5"
                opacity={visibleLines.leads ? 0.5 : 0.1}
              />
              <Line
                type="monotone"
                dataKey="planAppointments"
                stroke="#9C27B0"
                strokeWidth={1.5}
                dot={false}
                activeDot={false}
                strokeDasharray="5 5"
                opacity={visibleLines.appointments ? 0.5 : 0.1}
              />
              <Line
                type="monotone"
                dataKey="planVisitors"
                stroke="#2196F3"
                strokeWidth={1.5}
                dot={false}
                activeDot={false}
                strokeDasharray="5 5"
                opacity={visibleLines.visitors ? 0.5 : 0.1}
              />
              <Line
                type="monotone"
                dataKey="planSales"
                stroke="#E91E63"
                strokeWidth={1.5}
                dot={false}
                activeDot={false}
                strokeDasharray="5 5"
                opacity={visibleLines.sales ? 0.5 : 0.1}
              />
            </>
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
